/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Heading } from '@msdyn365-commerce-modules/utilities';
import { getRichTextHtml } from '@msdyn365-commerce/core';
import classname from 'classnames';
import * as React from 'react';
import { IContainerWithHeaderData } from './container-with-header.data';
import {
    IContainerWithHeaderConfig,
    IContainerWithHeaderProps,
    ITitleData
} from './container-with-header.props.autogenerated';

// ==============================================================================
/**
 * ContainerWithHeader component
 * @extends {React.PureComponent<IContainerWithHeaderProps<IContainerWithHeaderData>>}
 */
// ==============================================================================
class ContainerWithHeader extends React.PureComponent<IContainerWithHeaderProps<IContainerWithHeaderData>> {

    // ------------------------------------------------------
    // ------------------------------------------------------
    public render(): JSX.Element {
        const { config, slots } = this.props;
        const hasSlots = slots && slots.contentBlock && slots.contentBlock.length;

        return (
            <div className={classname('container-with-header', config.className)}>
                {config && this._renderHeader(config)}
                {slots.contentBlockNoHeadings && this._renderSlotItems(slots.contentBlockNoHeadings)}
                {hasSlots && this._renderSlotItems(slots.contentBlock)}
            </div>
        );
    }

    // ------------------------------------------------------
    // ------------------------------------------------------
    private _renderHeader(config: IContainerWithHeaderConfig): JSX.Element {
        return (
            <div className='container-with-header-header'>
                {config?.title && this._renderLinkedTitle(config)}
                {config?.text &&
                    // tslint:disable-next-line:react-no-dangerous-html
                    <div
                        className='container-with-header-text'
                        dangerouslySetInnerHTML={getRichTextHtml(config.text)}
                    />
                }
            </div>
        );
    }

    // ------------------------------------------------------
    // ------------------------------------------------------
    private _renderLinkedTitle(config: IContainerWithHeaderConfig): JSX.Element | null {
        if (config?.link?.linkUrl?.destinationUrl) {
            return (
                <a
                    className='container-with-header-title-link'
                    href={config.link.linkUrl.destinationUrl}
                    aria-label={config.link.ariaLabel}
                    target={config.link.openInNewTab ? '_blank' : undefined} rel="noreferrer"
                >
                    {this._renderTitle(config.title!)}
                </a>
            );
        } else {
            return this._renderTitle(config.title!);
        }
    }

    // ------------------------------------------------------
    // ------------------------------------------------------
    private _renderTitle(title: ITitleData): JSX.Element | null {
        if (title && title?.text) {
            return (
                <Heading
                    className='container-with-header-title'
                    headingTag={title.tag}
                    text={title.text}
                />
            );
        }
        return null;
    }

    // ------------------------------------------------------
    // ------------------------------------------------------
    private _renderSlotItems(items: React.ReactNode[]): JSX.Element {
        return (
            <div className='container-with-header-slots'>
                {items.map((slot: React.ReactNode, index: number) => (
                    <React.Fragment key={index}>
                        {slot}
                    </React.Fragment>
                ))}
            </div>
        );
    }
}

export default ContainerWithHeader;